/**
 * 장비.
 *
 * 누가 입는가부터 정한다. **장수와 동료만 입는다.** 일반 병사의 장비는 병종이
 * 대신한다. 도부수와 개마무사의 차이가 곧 장비 차이다.
 *
 * 수백 명에게 개별 장비를 입히면 화면이 감당되지 않고, 더 중요하게는 편성의
 * 고민이 병종 선택에서 장비 최적화로 옮겨간다. 이 게임의 중심은 누구를 데려가
 * 어디에 세우느냐이지 누구에게 무엇을 입히느냐가 아니다.
 *
 * 대신 군기를 슬롯으로 둔다. 군기는 개인이 아니라 분대 전체에 걸린다.
 * 그래서 장비를 고르는 일이 분대 편성과 맞물린다.
 */
import { Branch, Culture, HERO_TIER, HERO_WEAPON, LEADER_BASE, NO_CAPTAIN, Weapon, computePower, isRanged, rearm } from './troops.ts';
import type { CaptainBonus, Stats } from './troops.ts';
import type { Rng } from './rng.ts';

export const Slot = {
  Weapon: 'weapon',
  Armor: 'armor',
  Helm: 'helm',
  Mount: 'mount',
  Banner: 'banner',
  Shield: 'shield',
} as const;
export type Slot = (typeof Slot)[keyof typeof Slot];

export const SLOT_NAMES: Record<Slot, string> = {
  [Slot.Weapon]: '무기',
  [Slot.Armor]: '갑옷',
  [Slot.Helm]: '투구',
  [Slot.Mount]: '말',
  [Slot.Banner]: '군기',
  [Slot.Shield]: '방패',
};

/**
 * 능력치 보정. 전부 정수 덧셈이다. cd는 음수가 빠른 것이다. 사거리는 없다 — 무기 종류(`EquipDef.weapon`)가 정한다.
 * 타입에서 빼 두어야 무기나 다른 장비에 사거리 보정을 다시 적는 일(출처가 둘이 되는 일)이 컴파일에서 막힌다.
 */
export type StatMods = Partial<Omit<Stats, 'range'>>;

/**
 * 어디서 나오는가.
 *
 * 기본은 상점에서 산다. 좋은 것은 깊은 곳에서만 나온다. 돈으로 살 수 있는 물건에
 * 천장을 두지 않으면 깊이 들어갈 이유가 사라지고, 반대로 전부 드롭으로만 두면
 * 초반에 아무것도 못 갖춘 채 도박부터 해야 한다.
 */
export const Source = {
  Shop: 'shop',
  Depth: 'depth',
  /** 영토전 시즌의 금 등급 보상(`meta/season.ts`)에서만 나온다 — 전리품·상점에 없다. */
  Season: 'season',
} as const;
export type Source = (typeof Source)[keyof typeof Source];

export interface EquipDef {
  readonly id: string;
  readonly name: string;
  readonly hanja: string;
  readonly slot: Slot;
  readonly tier: number;
  /** 문화 전용이면 그 문화. 공통이면 null. */
  readonly culture: Culture | null;
  readonly mods: StatMods;
  /**
   * 무기 칸 아이템이 무엇인가(`WEAPONS`). 들면 사거리와 공격 성격(공격·간격·관통)이 이 무기의 것으로 바뀐다 — 병사와 같은
   * 규칙(`rearm`)이다. 아이템의 `mods`에는 **품질**(명품이라 더 센 몫)만 남는다. 예전에는 아이템마다 성격까지 손으로
   * 적었다(철모 간격 +2, 철부 간격 +3) — 무기 표의 도끼를 고쳐도 철부는 따라가지 않았다.
   */
  readonly weapon?: Weapon;
  /**
   * 착용자가 이끄는 분대 전체에 걸리는 보정.
   *
   * 군기만 갖는 것이 아니다. 영웅은 사기가 이미 최대라 절대 무너지지 않으므로
   * 개인 사기 보정은 죽은 값이다. 이름난 칼이나 금관이 주는 것은 본인의 담력이
   * 아니라 그걸 보는 병사들의 담력이다. 그래서 사기는 전부 이쪽으로 보낸다.
   */
  readonly command?: Partial<CaptainBonus>;
  readonly cost: number;
  readonly source: Source;
  /** 깊이에서 나오는 물건이 처음 등장하는 깊이. 상점 물건은 0이다. */
  readonly minDepth: number;
  /** 실존 유물이나 기록에서 따왔는가. 순수 창작이면 false. */
  readonly real: boolean;
  /**
   * 전투력 지표가 이 물건의 값을 제대로 재지 못한다는 표시.
   *
   * 근접 사거리와 속도는 실제 싸움에서 크게 작용하지만 계산식에 거의 들어가지
   * 않는다. 표시가 없으면 값어치 백분율만 보고 쓸모없는 물건으로 읽힌다.
   */
  readonly metricBlind?: string;
  /**
   * 방패가 앞에서 오는 화살을 막는 수(`SHIELD_BY_TIER`와 같은 규칙, troops.ts). 방패 칸 물건만 갖는다. 다 막으면
   * 부서지고, 든 동안은 병사의 방패와 같은 벌점(`SHIELD_PENALTY`)이 붙는다.
   */
  readonly blocks?: number;
  /**
   * 착용자가 이끄는 분대의 궁수가 벌점을 받지 않는 날씨(전장 특성 id: 'rain'·'night'·'fog'). Todo5 설계의 "특성 짝
   * 장비" — 군수품(기름 먹인 시위·횃불)은 한 판에 부대 전체, 이것은 늘 한 분대다. 판정 값이라 명부의 착장에서 나온다.
   */
  readonly weatherproof?: readonly string[];
  readonly note: string;
}

const TIER_COST: Record<number, number> = { 1: 60, 2: 200, 3: 700, 4: 2000, 5: 5000 };

/**
 * 티어별 출처와 등장 깊이. 1~2티어는 상점, 3티어부터는 깊은 곳에서만 나온다.
 *
 * `weight`는 **티어 단위**다. 물건 단위로 두면 같은 티어에 물건을 하나 더 넣는
 * 것만으로 그 티어 전체가 흔해진다. 처음에는 물건 단위였는데, 3티어가 9종이고
 * 5티어가 1종이라 "티어별 희귀도"라고 적어둔 문서와 실제 확률이 어긋났다.
 * 지금은 티어를 먼저 뽑고 그 안에서 균등하게 뽑으므로 물건을 더해도 그 티어가
 * 나올 확률은 그대로다.
 */
const TIER_SOURCE: Record<number, { source: Source; minDepth: number; weight: number }> = {
  1: { source: Source.Shop, minDepth: 0, weight: 0 },
  2: { source: Source.Shop, minDepth: 0, weight: 0 },
  // 깊이가 30까지 늘며(`MAX_DEPTH`) 3·5·8에서 5·12·20으로 옮겼다. 적 곡선이 완만해져 옛 깊이
  // 3의 싸움이 이제 깊이 10 언저리다. 그대로 두면 첫 출정부터 매 판 전리품이 쏟아진다.
  3: { source: Source.Depth, minDepth: 5, weight: 78 },
  4: { source: Source.Depth, minDepth: 12, weight: 21 },
  5: { source: Source.Depth, minDepth: 20, weight: 1 },
};

/** 전리품으로만 나오는 티어. 오름차순 고정이어야 주사위 배정이 안 흔들린다. */
const DEPTH_TIERS = [3, 4, 5];

/** 전리품이 처음 나오는 깊이. 따로 적어 두면 TIER_SOURCE와 어긋난다. */
export const DROP_MIN_DEPTH = Math.min(...DEPTH_TIERS.map((t) => TIER_SOURCE[t].minDepth));

interface Draft {
  id: string;
  name: string;
  hanja: string;
  slot: Slot;
  tier: number;
  culture?: Culture;
  mods: StatMods;
  weapon?: Weapon;
  command?: Partial<CaptainBonus>;
  real?: boolean;
  metricBlind?: string;
  blocks?: number;
  weatherproof?: readonly string[];
  /** 시즌 전용 장비(`Source.Season`) — 깊이 전리품·상점에 없다. */
  season?: boolean;
  note: string;
}

function make(d: Draft): EquipDef {
  const cost = TIER_COST[d.tier];
  const src = TIER_SOURCE[d.tier];
  if (cost === undefined || src === undefined) {
    throw new Error(`값이 정해지지 않은 티어입니다: ${d.id} T${d.tier}`);
  }
  return {
    id: d.id,
    name: d.name,
    hanja: d.hanja,
    slot: d.slot,
    tier: d.tier,
    culture: d.culture ?? null,
    mods: d.mods,
    weapon: d.weapon,
    command: d.command,
    cost,
    source: d.season ? Source.Season : src.source,
    minDepth: d.season ? 0 : src.minDepth,
    real: d.real !== false,
    metricBlind: d.metricBlind,
    blocks: d.blocks,
    weatherproof: d.weatherproof,
    note: d.note,
  };
}

/** 활의 값어치 안내. 멀리서 치는 몫은 전투력 지표에 없고, 간격이 길어 지표로는 칼보다 낮게 나온다. */
const BOW_BLIND = '멀리서 먼저 치는 몫은 전투력에 들지 않는다. 걷는 사람만 쏜다 — 말 위에서는 쓰지 못한다. 서 있을 때만 쏘고, 화살은 한 판에 30발이다.';

/** 방패의 값어치 안내. 막는 몫과 벌점은 능력치가 아니라 전투력 지표에 들지 않는다. */
const SHIELD_BLIND = '화살을 막는 몫은 전투력에 들지 않는다. 든 동안 공격력 −10%·공격 간격 +10%, 부서지면 사라진다.';

export const EQUIPMENT: readonly EquipDef[] = [
  // ---- 무기 ----
  make({
    id: 'w_cheoldo', name: '철도', hanja: '鐵刀', slot: Slot.Weapon, tier: 1,
    weapon: Weapon.Sword,
    mods: { atk: 2 },
    note: '장식 없는 쇠칼. 병사가 쓰는 것과 다르지 않다.',
  }),
  make({
    id: 'w_hwandudo', name: '환두대도', hanja: '環頭大刀', slot: Slot.Weapon, tier: 2,
    weapon: Weapon.Sword,
    mods: { atk: 4, cd: -1 },
    note: '고리자루큰칼. 삼국이 모두 썼고 무덤에서 가장 많이 나온다.',
  }),
  make({
    // 이름만 '철모'면 쇠 투구(鐵帽)로 읽힌다 — 플레이한 사람이 환두대도와 값이 같은 투구로 알았다.
    id: 'w_cheolmo', name: '철모(쇠창)', hanja: '鐵矛', slot: Slot.Weapon, tier: 2,
    weapon: Weapon.Spear,
    mods: { atk: 10, pierce: 1 },
    metricBlind: '사거리를 값으로 치지 않는다. 먼저 때리고 덜 맞는 몫이 빠져 있다.',
    note: '쇠창. 멀리 닿지만 한 번 내지르는 데 시간이 든다.',
  }),
  make({
    id: 'w_cheolbu', name: '철부', hanja: '鐵斧', slot: Slot.Weapon, tier: 3,
    weapon: Weapon.Axe,
    mods: { atk: 5, def: -1, pierce: 4 },
    note: '쇠도끼. 한 방이 무겁지만 휘두르는 사이 몸이 열린다. 갑옷을 깨는 무기라 관통이 붙는다. 방어 페널티는 상한에 흡수되지 않고 끝까지 남는다.',
  }),
  make({
    id: 'w_samyeop', name: '삼엽환두대도', hanja: '三葉環頭大刀', slot: Slot.Weapon, tier: 3,
    weapon: Weapon.Sword,
    mods: { atk: 7, cd: -1 },
    command: { gritBonus: 10 },
    note: '고리 안에 세 잎 무늬를 넣은 대도. 지휘관급이 찼다.',
  }),
  make({
    id: 'w_yongbong', name: '용봉문환두대도', hanja: '龍鳳文環頭大刀', slot: Slot.Weapon, tier: 4,
    weapon: Weapon.Sword,
    mods: { atk: 11, cd: -2, pierce: 2 },
    command: { gritBonus: 25 },
    note: '고리에 용과 봉황을 새긴 칼. 왕과 최고위 무장의 것이다.',
  }),
  make({
    id: 'w_chiljido', name: '칠지도', hanja: '七支刀', slot: Slot.Weapon, tier: 5,
    culture: Culture.Baekje,
    weapon: Weapon.Sword,
    mods: { atk: 14, cd: -2 },
    command: { atkPct: 5, gritBonus: 45 },
    note: '가지가 일곱인 칼. 백제가 왜에 보낸 것으로 명문이 남아 있다. 실전 무기라기보다 상징이라 게임에서는 사기를 크게 올린다.',
  }),

  // ---- 갑옷 ----
  make({
    id: 'a_pigap', name: '피갑', hanja: '皮甲', slot: Slot.Armor, tier: 1,
    mods: { hp: 40, def: 1 },
    note: '가죽갑옷. 가볍고 싸다.',
  }),
  make({
    id: 'a_chalgap', name: '찰갑', hanja: '札甲', slot: Slot.Armor, tier: 2,
    mods: { hp: 90, def: 2, speed: -2 },
    note: '작은 쇳조각을 엮은 비늘갑옷. 고구려 기병의 표준이다.',
  }),
  make({
    id: 'a_pangap', name: '판갑', hanja: '板甲', slot: Slot.Armor, tier: 2,
    mods: { hp: 70, def: 3, speed: -6, mass: 1 },
    note: '넓은 쇠판을 이어 붙인 갑옷. 잘 막지만 몸이 굳는다.',
  }),
  make({
    id: 'a_geumdong', name: '금동장 찰갑', hanja: '金銅裝札甲', slot: Slot.Armor, tier: 3,
    mods: { hp: 150, def: 3, speed: -2 },
    command: { gritBonus: 20 },
    note: '금동으로 장식한 찰갑. 멀리서도 지휘관이 어디 있는지 보인다.',
  }),
  make({
    id: 'a_gaema', name: '개마 찰갑', hanja: '鎧馬札甲', slot: Slot.Armor, tier: 4,
    mods: { hp: 240, def: 5, speed: -6, mass: 2 },
    note: '사람과 말을 함께 덮는 중장갑. 부딪히면 대열이 열린다.',
  }),

  // ---- 투구 ----
  make({
    id: 'h_gajuk', name: '가죽투구', hanja: '皮冑', slot: Slot.Helm, tier: 1,
    mods: { hp: 20 },
    note: '머리를 덮는 최소한의 것.',
  }),
  make({
    id: 'h_jongjang', name: '종장판주', hanja: '縱長板冑', slot: Slot.Helm, tier: 2,
    mods: { hp: 40, def: 1 },
    command: { gritBonus: 15 },
    note: '세로로 긴 쇠판을 둘러 만든 투구.',
  }),
  make({
    id: 'h_balhyeong', name: '몽고발형주', hanja: '蒙古鉢形冑', slot: Slot.Helm, tier: 3,
    mods: { hp: 70, def: 1 },
    command: { gritBonus: 30 },
    note: '사발을 엎은 모양의 투구. 북방 기병의 것이다.',
  }),
  make({
    id: 'h_gwanmo', name: '금동관모', hanja: '金銅冠帽', slot: Slot.Helm, tier: 4,
    mods: { hp: 90, def: 1 },
    command: { defBonus: 1, gritBonus: 55 },
    note: '금동으로 만든 관. 쓰고 있으면 부대가 지휘관을 잃지 않는다.',
  }),
  make({
    id: 'h_cheonma', name: '천마총 금관', hanja: '天馬塚金冠', slot: Slot.Helm, tier: 5,
    culture: Culture.Silla,
    mods: { hp: 110, def: 1 },
    command: { defBonus: 1, gritBonus: 70 },
    note: '경주 천마총에서 나온 금관. 出자 모양 세움장식에 굽은옥을 달았다. 실전 투구가 아니라 왕의 상징이라, 머리를 지키기보다 그걸 보는 병사들을 붙잡아 둔다.',
  }),

  // ---- 말 ----
  make({
    id: 'm_joranmal', name: '조랑말', hanja: '果下馬', slot: Slot.Mount, tier: 1,
    mods: { speed: 4, mass: 1 },
    note: '작고 단단한 토종말. 산길을 잘 간다.',
  }),
  make({
    id: 'm_gunma', name: '군마', hanja: '軍馬', slot: Slot.Mount, tier: 2,
    mods: { hp: 40, speed: 10, mass: 2 },
    note: '훈련된 전마. 사람 사이를 겁내지 않는다.',
  }),
  make({
    id: 'm_deungja', name: '등자 갖춘 군마', hanja: '鐙子軍馬', slot: Slot.Mount, tier: 3,
    mods: { hp: 60, atk: 3, speed: 12, mass: 3 },
    note: '등자를 단 말. 발을 걸고 버틸 수 있어 말 위에서 힘을 쓴다. 고구려 고분벽화에 이미 보인다.',
  }),
  make({
    id: 'm_gapjuma', name: '갑주마', hanja: '甲冑馬', slot: Slot.Mount, tier: 3,
    mods: { hp: 120, def: 1, speed: 6, mass: 4 },
    note: '마갑을 씌운 말. 화살에 쉽게 쓰러지지 않는다.',
  }),
  make({
    id: 'm_gaema', name: '개마', hanja: '鎧馬', slot: Slot.Mount, tier: 4,
    mods: { hp: 200, def: 3, speed: 4, mass: 6 },
    note: '사람도 말도 철갑을 두른 상태. 이 게임의 이름 후보였던 말이다.',
  }),
  make({
    id: 'm_junma', name: '준마', hanja: '駿馬', slot: Slot.Mount, tier: 4,
    mods: { hp: 60, speed: 28, mass: 1 },
    real: false,
    metricBlind: '속도가 거의 반영되지 않는다. 붙고 빠지는 값이 빠져 있다.',
    note: '유난히 빠른 말. 치고 빠지는 운용을 위한 선택지로 넣었다.',
  }),

  // ---- 군기 ----
  make({
    id: 'b_gi', name: '기', hanja: '旗', slot: Slot.Banner, tier: 1,
    mods: {},
    command: { gritBonus: 10 },
    note: '색천을 단 장대. 어디로 모일지 알려준다.',
  }),
  make({
    id: 'b_suja', name: '수자기', hanja: '帥字旗', slot: Slot.Banner, tier: 2,
    mods: {},
    command: { atkPct: 4, gritBonus: 15 },
    real: false,
    note: '장수를 뜻하는 글자를 새긴 기. 후대의 것을 앞당겨 썼다.',
  }),
  make({
    id: 'b_samjogo', name: '삼족오기', hanja: '三足烏旗', slot: Slot.Banner, tier: 3,
    culture: Culture.Goguryeo,
    mods: {},
    command: { atkPct: 8, gritBonus: 25 },
    note: '세 발 까마귀는 고구려가 해를 나타낸 상징이다. 고분벽화에 거듭 나온다.',
  }),
  make({
    id: 'b_yongmun', name: '용문기', hanja: '龍文旗', slot: Slot.Banner, tier: 3,
    culture: Culture.Baekje,
    mods: {},
    command: { defBonus: 1, gritBonus: 30 },
    real: false,
    note: '백제 금속 공예의 용무늬에서 따왔다. 기 자체는 창작이다.',
  }),
  make({
    id: 'b_hwarang', name: '화랑기', hanja: '花郎旗', slot: Slot.Banner, tier: 3,
    culture: Culture.Silla,
    mods: {},
    command: { gritBonus: 50 },
    real: false,
    note: '화랑 무리를 나타내는 기. 신라 분대는 좀처럼 무너지지 않는다.',
  }),
  make({
    id: 'b_daejang', name: '대장기', hanja: '大將旗', slot: Slot.Banner, tier: 4,
    mods: {},
    command: { atkPct: 10, defBonus: 1, gritBonus: 40 },
    real: false,
    note: '전군의 중심이 되는 기. 이것이 쓰러지면 전선 전체가 흔들린다.',
  }),
  make({
    id: 'b_hotaewang', name: '호태왕 청동방울', hanja: '好太王靑銅鈴', slot: Slot.Banner, tier: 5,
    culture: Culture.Goguryeo,
    mods: {},
    command: { atkPct: 12, defBonus: 1, gritBonus: 60 },
    note: '2003년 태왕릉에서 나온 청동 방울. 「辛卯年好太王○造鈴九十六」— 신묘년(391)에 호태왕이 만든 아흔여섯 번째 방울이라는 뜻이다. 이 무덤이 광개토대왕의 것임을 확정지은 유물이자, 광개토대왕 본인과 직접 이어지는 유일한 착용 가능한 물건이다. 아흔여섯 개를 만들어 돌렸다는 것은 신호를 전하는 도구였다는 말이므로, 게임에서는 전부 분대 지휘로 간다.',
  }),

  // ---- 방패 ----
  // 맨 뒤에 둔다. 아이콘 시트(`public/sprites/items.png`)가 이 목록의 순서대로 늘어서 있어, 중간에 넣으면 뒤의
  // 아이콘이 모두 한 칸씩 밀린다. 플레이한 사람이 청했다: "장수의 경우는 방패 슬롯을 추가해주면 되겠고."
  make({
    id: 's_moksun', name: '나무 방패', hanja: '木楯', slot: Slot.Shield, tier: 1,
    mods: {}, blocks: 8, metricBlind: SHIELD_BLIND,
    note: '나무판을 이어 가죽을 씌운 방패. 안악 3호분 행렬도의 보병이 든 것과 같은 꼴이다.',
  }),
  make({
    id: 's_cheoltae', name: '쇠테 방패', hanja: '鐵緣楯', slot: Slot.Shield, tier: 2,
    mods: {}, blocks: 14, metricBlind: SHIELD_BLIND,
    note: '가장자리를 쇠띠로 두른 방패. 쪼개지지 않아 오래 버틴다.',
  }),
  make({
    id: 's_cheolpan', name: '철판 방패', hanja: '鐵板楯', slot: Slot.Shield, tier: 3,
    mods: {}, blocks: 22, metricBlind: SHIELD_BLIND, real: false,
    note: '앞면에 쇠판을 덧댄 무거운 방패. 판갑을 만들던 솜씨로 만들었다고 보았다.',
  }),
  make({
    id: 's_geumdong', name: '금동장 방패', hanja: '金銅裝楯', slot: Slot.Shield, tier: 4,
    mods: {}, blocks: 30, command: { gritBonus: 20 }, metricBlind: SHIELD_BLIND, real: false,
    note: '금동 돌기와 테를 박은 지휘관의 방패. 화살비 속에서도 번쩍여 병사들이 장수가 서 있음을 안다.',
  }),
  // 특성 짝 장비(Todo5 7단계). 맨 뒤에 붙인다 — 아이콘 시트(`items.png`)의 칸 순서가 이 순서다. 날씨 하나를 한 분대에서
  // 지운다(`weatherproof`). 전리품에서는 그 날씨의 판에 이 칸이 더 잘 나온다(`TRAIT_LOOT`).
  make({
    id: 'h_satgat', name: '도롱이 삿갓', hanja: '蓑笠', slot: Slot.Helm, tier: 3,
    mods: { hp: 40 }, weatherproof: ['rain'], real: false,
    note: '짚으로 엮은 삿갓과 도롱이. 빗속에서도 활시위를 마르게 둘 줄 아는 지휘관의 것이다.',
  }),
  make({
    id: 'b_deungrong', name: '등롱기', hanja: '燈籠旗', slot: Slot.Banner, tier: 3,
    mods: {}, command: { gritBonus: 15 }, weatherproof: ['night'], real: false,
    note: '장대 끝에 등롱을 단 기. 밤에도 분대가 지휘관 자리를 보고 과녁을 가린다.',
  }),
  make({
    id: 'b_gakjeok', name: '뿔나팔', hanja: '角笛', slot: Slot.Banner, tier: 3,
    mods: {}, command: { gritBonus: 15 }, weatherproof: ['fog'], real: false,
    note: '쇠뿔로 만든 나팔. 안개 속에서도 소리로 적이 어디 있는지 알려 궁수가 헛쏘지 않는다.',
  }),

  // ---- 시즌 전용(영토전 시즌의 금 등급 보상, `meta/season.ts`의 `SEASON_ITEMS` 순서로 시즌마다 돈다) ----
  // 4티어 남짓의 세기에 저마다 다른 결. 세 나라 모두 입는다. 아이콘 시트가 이 목록 순서라 맨 뒤에 둔다.
  make({
    id: 'w_chilseong', name: '칠성검', hanja: '七星劍', slot: Slot.Weapon, tier: 4, season: true,
    weapon: Weapon.Sword,
    mods: { atk: 12, cd: -2, pierce: 2 },
    command: { atkPct: 5 },
    note: '칼몸에 북두칠성 일곱 점을 금으로 새긴 곧은 검. 별을 따라 싸운다는 믿음이 휘하의 칼끝을 무겁게 한다. 시즌 보상.',
  }),
  make({
    id: 'h_jougwan', name: '조우관', hanja: '鳥羽冠', slot: Slot.Helm, tier: 4, season: true,
    mods: { hp: 80, def: 1 },
    command: { atkPct: 5, gritBonus: 35 },
    note: '고구려 벽화의 새깃 관. 높이 솟은 두 깃이 멀리서도 보여 병사들이 지휘관을 놓치지 않는다. 시즌 보상.',
  }),
  make({
    id: 'b_sasin', name: '사신기', hanja: '四神旗', slot: Slot.Banner, tier: 4, season: true,
    mods: {}, command: { atkPct: 8, defBonus: 1, gritBonus: 45 }, real: false,
    note: '청룡·백호·주작·현무를 그린 기. 네 방위의 수호신이 대열을 지킨다. 시즌 보상.',
  }),
  make({
    id: 'w_gajichang', name: '가지창', hanja: '枝槍', slot: Slot.Weapon, tier: 4, season: true,
    weapon: Weapon.Spear,
    mods: { atk: 9, cd: -1, pierce: 3 },
    command: { defBonus: 1 },
    note: '창날 옆으로 갈고리 가지가 뻗은 창. 찌르고 걸어 당겨 갑옷 틈을 연다. 시즌 보상.',
  }),
  make({
    id: 'a_eorin', name: '어린갑', hanja: '魚鱗甲', slot: Slot.Armor, tier: 4, season: true,
    mods: { hp: 200, def: 4, speed: -3 },
    note: '물고기 비늘처럼 작은 철편을 겹친 갑옷. 개마 찰갑보다 가볍고 금동장 찰갑보다 단단하다. 시즌 보상.',
  }),
  make({
    id: 'b_cheongo', name: '천고', hanja: '天鼓', slot: Slot.Banner, tier: 4, season: true,
    mods: {}, command: { atkPct: 6, gritBonus: 60 }, real: false,
    note: '붉게 칠한 큰 북. 하늘을 울리는 소리에 무너지던 대열도 다시 선다. 시즌 보상.',
  }),

  // ---- 활 ----
  // 장수·부하장수가 드는 쏘는 무기(플레이한 사람: "장수도 활을 쓸 수 있게 해줘. 상점에서도 팔고 드롭아이템으로도
  // 추가하고"). 걷는 사람이 들면 궁수의 가지가 되어 병사 궁수와 같은 규칙으로 쏜다(`scenario.mountUp`, `shoots`).
  // 무기 표의 4등급 성격이 있는 것만 든다(`HERO_TIER`) — 사냥활은 1등급뿐이라 영웅의 것이 아니다.
  //
  // **목록의 맨 뒤에 둔다.** 아이콘 시트(`public/sprites/items.png`)는 이 목록의 순서가 곧 칸 자리다(`ui/items.ts`).
  // 처음에는 무기 구획(갑옷 앞)에 끼워 넣어 갑옷부터 뒤의 모든 아이콘이 다섯 칸씩 밀렸다 — 피갑이 투구 그림, 군마가
  // 깃발 그림으로 나왔다(리뷰·QA). 시트의 줄 수가 같아 크기 시험은 통과했다. 이제 시험이 칸마다의 id를 본다
  // (`items.ids.json`).
  make({
    id: 'w_dangung', name: '단궁', hanja: '短弓', slot: Slot.Weapon, tier: 1,
    weapon: Weapon.Bow,
    mods: {},
    metricBlind: BOW_BLIND,
    note: '짧은 나무 활. 삼국의 병사가 두루 쓰던 것이다.',
  }),
  make({
    id: 'w_gakgung', name: '각궁', hanja: '角弓', slot: Slot.Weapon, tier: 2,
    weapon: Weapon.Bow,
    mods: { atk: 3 },
    metricBlind: BOW_BLIND,
    note: '물소 뿔과 힘줄을 덧댄 활. 작아도 멀리 나간다.',
  }),
  make({
    id: 'w_maekgung', name: '맥궁', hanja: '貊弓', slot: Slot.Weapon, tier: 3,
    culture: Culture.Goguryeo,
    weapon: Weapon.Maekgung,
    mods: { atk: 5, cd: -1 },
    metricBlind: BOW_BLIND,
    note: '고구려의 좋은 활. 중국의 기록이 "맥궁이 여기서 난다"고 적었다. 활보다 멀리 나간다.',
  }),
  make({
    id: 'w_no', name: '노(쇠뇌)', hanja: '弩', slot: Slot.Weapon, tier: 3,
    weapon: Weapon.Crossbow,
    mods: { atk: 4 },
    metricBlind: BOW_BLIND,
    note: '틀에 얹어 쏘는 활. 느리지만 한 발이 무겁고 가장 멀리 간다.',
  }),
  make({
    id: 'w_cheonbono', name: '천보노', hanja: '千步弩', slot: Slot.Weapon, tier: 4,
    culture: Culture.Silla,
    weapon: Weapon.Crossbow,
    mods: { atk: 9, cd: -2 },
    metricBlind: BOW_BLIND,
    note: '신라의 구진천이 만든 쇠뇌. 천 보를 간다 하여 당이 그 기술을 탐냈다.',
  }),
];

export const EQUIP_BY_ID: ReadonlyMap<string, EquipDef> = new Map(
  EQUIPMENT.map((e) => [e.id, e]),
);

// ---------------------------------------------------------------------------
// 강화(Todo10, 사용자가 고른 권장안 — 같은 물건 + 냥, ★1~3, 실패 없음)
// ---------------------------------------------------------------------------

/** 강화 단계의 끝. */
export const ENHANCE_MAX = 3;
/** 단계마다 몸 수치(공격·체력·관통)와 방패가 막는 수에 더하는 몫(%). 0이 아니면 단계마다 최소 1. */
export const ENHANCE_PCT = 15;
/** 단계마다 분대 보정에 더하는 몫: 공격 보정 %p와 사기. 그 보정을 가진 물건에만(군기는 몸 수치가 없다). */
export const ENHANCE_CMD_ATK = 1;
export const ENHANCE_CMD_GRIT = 5;

/**
 * 강화한 물건. 창고·착장에는 **열쇠**(`w_chilseong+2`)로 적는다 — 창고는 id 목록이고, 같은 물건이라도 단계가 다르면 다른
 * 물건이다. `id`가 그 열쇠이고(화면이 입히고 파는 열쇠를 그대로 쓴다), 그림·아이콘은 `baseId`로 찾는다. `mods`·`command`·
 * `blocks`는 단계를 얹은 값이다 — 착장 적용(`applyLoadout`)·값어치(`equipGainPct`)·툴팁이 그대로 읽는다.
 *
 * 방어는 올리지 않는다. 장비 방어 상한(+6)이 불사를 막는 장치라 강화가 거기 닿으면 안 된다.
 */
export interface EquipItem extends EquipDef {
  readonly baseId: string;
  readonly star: number;
}

/** 열쇠의 단계. 단계가 없으면 0, 단계 표기가 틀리면 -1. */
export function itemStar(key: string): number {
  const i = key.lastIndexOf('+');
  if (i < 0) return 0;
  const tail = key.slice(i + 1);
  // `Number('')`는 0이라 빈 꼬리를 따로 막는다.
  const n = /^[0-9]$/.test(tail) ? Number(tail) : -1;
  return n >= 1 && n <= ENHANCE_MAX ? n : -1;
}

/** 열쇠의 바탕 물건 id. */
export function itemBase(key: string): string {
  const i = key.lastIndexOf('+');
  return i < 0 ? key : key.slice(0, i);
}

/** 바탕 id와 단계로 열쇠. 0단계는 id 그대로 — 옛 저장·명부와 같다. */
export function itemKey(id: string, star: number): string {
  return star > 0 ? `${id}+${star}` : id;
}

/** 단계 몫: 양수 값에만, 단계마다 `ENHANCE_PCT`%(내림), 최소 단계 수만큼. 정수만. */
function boosted(v: number, star: number): number {
  return v > 0 ? v + Math.max(star, Math.floor((v * ENHANCE_PCT * star) / 100)) : v;
}

const ITEM_CACHE = new Map<string, EquipItem>();

/**
 * 열쇠로 물건을 찾는다(단계를 얹은 값). 모르는 물건·틀린 단계는 undefined. 착장·창고의 열쇠는 모두 이것으로 읽는다 —
 * `EQUIP_BY_ID`는 바탕 물건만 안다.
 */
export function equipOf(key: string | null | undefined): EquipItem | undefined {
  if (typeof key !== 'string' || key === '') return undefined;
  const hit = ITEM_CACHE.get(key);
  if (hit) return hit;
  const star = itemStar(key);
  const base = EQUIP_BY_ID.get(itemBase(key));
  if (!base || star < 0) return undefined;
  let item: EquipItem;
  if (star === 0) item = { ...base, baseId: base.id, star: 0 };
  else {
    const mods: StatMods = { ...base.mods };
    for (const k of ['atk', 'hp', 'pierce'] as const) if (mods[k] !== undefined) mods[k] = boosted(mods[k] ?? 0, star);
    const c = base.command;
    item = {
      ...base,
      id: itemKey(base.id, star),
      // 이름에 단계를 붙인다 — 창고·착장·결과(잃은 것)·툴팁이 모두 이름을 읽으므로 한 곳에서.
      name: `${base.name} ★${star}`,
      baseId: base.id,
      star,
      mods,
      ...(c
        ? {
            command: {
              ...c,
              ...((c.atkPct ?? 0) > 0 ? { atkPct: (c.atkPct ?? 0) + ENHANCE_CMD_ATK * star } : {}),
              ...((c.gritBonus ?? 0) > 0 ? { gritBonus: (c.gritBonus ?? 0) + ENHANCE_CMD_GRIT * star } : {}),
            },
          }
        : {}),
      ...(base.blocks !== undefined ? { blocks: boosted(base.blocks, star) } : {}),
    };
  }
  ITEM_CACHE.set(key, item);
  return item;
}

/**
 * 다음 단계로 올리는 데 드는 재료(같은 물건 ★ 없는 것)의 수: **단계마다 앞 단계의 두 배** — ★1에 1개, ★2에 2개, ★3에 4개
 * (모두 일곱). 처음에는 단계마다 하나였다. 플레이한 사람이 정했다: "강화 단계가 올라갈수록 요구되는 동일 장비의 수량을
 * 이전의 두 배로 해줘." 높은 단계가 냥만이 아니라 같은 물건을 얼마나 모았는가에도 달리게 된다.
 */
export function enhanceMaterials(nextStar: number): number {
  return nextStar >= 1 ? 2 ** (nextStar - 1) : 0;
}

/**
 * 다음 단계로 올리는 값(냥): **단계마다 앞 단계의 두 배** — ★1이 그 물건 값의 절반, ★2가 그 두 배, ★3이 네 배. 3티어면
 * 350·700·1,400냥. 처음에는 단계에 비례했다(350·700·1,050). 재료를 단계마다 두 배로 하며(`enhanceMaterials`) 플레이한
 * 사람이 값도 같이 정했다: "가격도 두 배로 해줘."
 */
export function enhanceCost(item: EquipDef, nextStar: number): number {
  return enhanceMaterials(nextStar) * Math.floor(item.cost / 2);
}

/** 한 벌. 군기도 한 칸이지만 능력치는 본인이 아니라 분대에 걸린다. */
export interface Loadout {
  weapon?: string;
  armor?: string;
  helm?: string;
  mount?: string;
  banner?: string;
  shield?: string;
}

/**
 * 칸의 고정 순서.
 *
 * 착장을 객체로 들고 다니는데, 판정이 객체 키 순서에 기대면 안 된다. 키 삽입
 * 순서는 어디서 만들었느냐에 따라 달라지고, 그러면 같은 착장인데 소실 주사위가
 * 다르게 배정된다. 순회는 늘 이 배열을 따른다.
 */
export const SLOT_ORDER = [
  Slot.Weapon,
  Slot.Armor,
  Slot.Helm,
  Slot.Mount,
  Slot.Banner,
  // 방패는 나중에 생긴 칸이라 맨 뒤다. 앞 칸들의 순서(전멸 때 잃는 주사위의 순서)를 흔들지 않는다.
  Slot.Shield,
] as const;

/** 본인 능력치에 얹히는 칸. 군기는 여기 없다. */
const PERSONAL_SLOTS = [Slot.Weapon, Slot.Armor, Slot.Helm, Slot.Mount] as const;

/** 착장을 고정 순서의 식별자 목록으로 펼친다. applyCommand에 그대로 넘긴다. */
export function loadoutIds(loadout: Loadout): (string | undefined)[] {
  return SLOT_ORDER.map((slot) => loadout[slot]);
}

/**
 * 믿을 수 없는 착장을 걸러 낸다.
 *
 * 리플레이는 아무 명부나 실어 올 수 있다. 모르는 물건, 칸이 어긋난 물건, 남의
 * 문화 전용이 그대로 들어오면 시뮬레이션이 군말 없이 재현해 버린다. 계정에 실제로
 * 있는 물건인지는 서버가 데이터베이스로 따로 본다. 여기서 보는 것은 규칙에 맞는
 * 착장이냐뿐이다.
 */
export function sanitizeLoadout(
  loadout: Loadout | null | undefined,
  culture: Culture,
): Loadout {
  const out: Loadout = {};
  if (!loadout) return out;
  for (const slot of SLOT_ORDER) {
    const id = loadout[slot];
    if (!id) continue;
    // 강화한 물건은 열쇠(`w_x+2`)로 온다. 단계가 틀리면 그 칸은 비운다(`equipOf`).
    const item = equipOf(id);
    if (!item || item.slot !== slot) continue;
    if (item.culture !== null && item.culture !== culture) continue;
    out[slot] = id;
  }
  return out;
}

/**
 * 장비로 올릴 수 있는 방어의 상한.
 *
 * 방어는 고정 차감이라 쌓으면 어느 순간 피해가 최소값으로 눌린다. 상한이 없으면
 * 완전 무장한 장수를 4티어 정예가 때려도 한 번에 1씩만 들어가 사실상 불사가 된다.
 * 그러면 언제 빠질 것인가를 고민할 이유가 사라지고 이 게임의 도박이 무너진다.
 * 상한을 두는 편이 방어 수치를 잘게 깎는 것보다 정직하다.
 */
export const EQUIP_DEF_CAP = 6;

/** 쏘는 무기(활·쇠뇌)를 들었는가. 말을 탔는지는 보지 않는다. */
export function holdsBow(loadout: Loadout): boolean {
  const held = equipOf(loadout.weapon)?.weapon;
  return held !== undefined && isRanged(held);
}

/**
 * 이 착장으로 **쏘는가**: 쏘는 무기를 들고 걷는다. 말 위에서는 쏘지 못한다 — 플레이한 사람과 정했다(2026-10-09). 가장
 * 빠른 몸이 멀리서 쏘며 달아나면 장수가 쓰러질 위험이 사라진다(병사 궁수의 "물러서며 쏘기"를 없앤 것과 같은 까닭).
 */
export function shoots(loadout: Loadout): boolean {
  return holdsBow(loadout) && !isMounted(loadout);
}

/**
 * 전장에서 실제로 쓰는 착장: 말을 타고 활을 들었으면 **활을 빼고** 본다(맨손 칼로 싸운다 — 활의 품질 보정도 지휘 보정도
 * 붙지 않는다). 능력치(`riderStats`)·지휘 보정·그림(`scenario.mountUp`)이 모두 이것을 거친다 — 한 곳만 빼면 화면은 활의
 * 사거리를 말하고 전장은 칼로 싸운다.
 */
export function fieldLoadout(loadout: Loadout): Loadout {
  if (!holdsBow(loadout) || !isMounted(loadout)) return loadout;
  const { weapon: _unused, ...rest } = loadout;
  return rest;
}

/** 장비를 입힌 능력치. 정수 덧셈만 한다. */
export function applyLoadout(base: Stats, loadout: Loadout): Stats {
  const out: Stats = { ...base };
  // 상한은 이득에만 건다. 양과 음을 따로 모으지 않으면 페널티가 상한에 흡수돼
  // 사라진다. 철부를 중장갑과 함께 들었을 때 "몸이 열린다"가 0이 되어버린다.
  let defUp = 0;
  let defDown = 0;
  // 무기를 바꿔 든다(병사와 같은 `rearm`): 맨손일 때 드는 칼에서 아이템의 무기 종류로. 품질 보정보다 먼저 — 품질은
  // 무기 성격의 비율에 곱해지지 않고 그대로 더해진다.
  const held = loadout.weapon ? equipOf(loadout.weapon)?.weapon : undefined;
  if (held) Object.assign(out, rearm(out, HERO_WEAPON, held, HERO_TIER));
  for (const slot of PERSONAL_SLOTS) {
    const id = loadout[slot];
    if (!id) continue;
    const item = equipOf(id);
    if (!item) continue;
    for (const k of Object.keys(item.mods) as (keyof StatMods)[]) {
      const v = item.mods[k] ?? 0;
      if (k === 'def') {
        if (v >= 0) defUp += v;
        else defDown += v;
        continue;
      }
      out[k] = out[k] + v;
    }
  }
  out.def = Math.max(0, base.def + Math.min(EQUIP_DEF_CAP, defUp) + defDown);
  // 아무리 벗겨도 0 이하로는 내려가지 않는다.
  out.cd = Math.max(4, out.cd);
  out.speed = Math.max(20, out.speed);
  out.hp = Math.max(1, out.hp);
  return out;
}

/** 착용한 것들이 분대에 주는 보정을 모두 더한다. 군기만이 아니다. */
export function applyCommand(
  base: CaptainBonus,
  ids: readonly (string | undefined)[],
): CaptainBonus {
  let out = base;
  for (const id of ids) {
    if (!id) continue;
    const item = equipOf(id);
    if (!item?.command) continue;
    out = {
      // 잘 부리는 병종의 몫(`favor`)은 그대로 실어 나른다 — 장비는 거기에 얹지 않는다. 처음에는 세 값만 새로 적어, 군기나
      // 이름난 칼을 든 부하장수의 병종 몫이 통째로 사라졌다(화면은 정의에서 읽어 여전히 있다고 적었다 — 리뷰가 잡았다).
      ...out,
      atkPct: out.atkPct + (item.command.atkPct ?? 0),
      defBonus: out.defBonus + (item.command.defBonus ?? 0),
      gritBonus: out.gritBonus + (item.command.gritBonus ?? 0),
    };
  }
  return out;
}

/**
 * 장비 값어치를 재는 기준 장수. **전장에 서는 그 몸이다.**
 *
 * 장비는 이 몸에 얹었을 때 전투력이 몇 퍼센트 오르는지로 값을 매긴다. 병종
 * 전투력과 같은 철학이다. 표에 적는 숫자가 아니라 수치에서 뽑는 값이어야
 * 수치를 고쳤을 때 값어치가 따라 움직인다.
 *
 * 여기에 따로 적은 몸(체력 520, 공격 18, 방어 4)을 두었던 적이 있다. 전장에
 * 없는 몸이라 값이 어긋났다. `troops.ts`의 `LEADER_BASE`가 유일한 출처다.
 */
export const BARE_HERO: Stats = LEADER_BASE;

/**
 * 말에 오른 영웅(장수·동료)에게 붙는 속도. 말마다 다른 몫(`mods`)과 따로, 걷다가
 * 올라탄 몫이다.
 *
 * 맨몸 장수가 걷게 되면서(`LEADER_BASE`) 생겼다. 말의 값어치에는 이 몫이 들어간다 —
 * 조랑말이 속도 +4짜리 물건이 아니라 걷던 장수를 태우는 물건이 되었다. 처음에는
 * 동료에게 붙지 않았다(동료의 몸은 늘 말 위였다). 동료도 말이 없으면 걷게 되면서
 * (`COMPANION_BASE`) 같은 몫이 붙는다.
 */
export const RIDING_SPEED = 28;

/** 착장한 방패가 막는 화살 수(`EquipDef.blocks`). 방패가 없으면 0. */
export function shieldBlocks(loadout: Loadout): number {
  const item = equipOf(loadout.shield);
  return item?.slot === Slot.Shield ? (item.blocks ?? 0) : 0;
}

/** 착장한 방패가 큰 방패인가(3티어 이상). 화면이 큰 방패 그림을 든다. */
export function shieldBig(loadout: Loadout): boolean {
  const item = equipOf(loadout.shield);
  return item?.slot === Slot.Shield && item.tier >= 3;
}

/** 말을 찼는가. 없는 물건 이름은 안 찬 것이다. */
export function isMounted(loadout: Loadout): boolean {
  return equipOf(loadout.mount)?.slot === Slot.Mount;
}

/**
 * 걷는 몸에 착장을 입힌 능력치. 말을 찼으면 올라탄 몫까지 더한다.
 * 장수와 동료가 같은 식을 쓴다 — 병기고 화면과 전장도 이것 하나를 본다.
 */
export function riderStats(body: Stats, loadout: Loadout): Stats {
  // 말 위의 활은 쓰지 않는 것으로 친다(`fieldLoadout`).
  const s = applyLoadout(body, fieldLoadout(loadout));
  if (isMounted(loadout)) s.speed += RIDING_SPEED;
  return s;
}

/** 장수의 착장 능력치. */
export function heroStats(loadout: Loadout): Stats {
  return riderStats(BARE_HERO, loadout);
}

/**
 * 영웅 기준 방어 환산값.
 *
 * 병종 전투력은 기준 공격 12로 실질 체력을 잰다. 영웅은 4티어 정예와 부딪히는데
 * 그쪽 공격이 16에서 18이라 12를 쓰면 방어 한 점의 값이 터무니없이 부풀려진다.
 * 그래서 영웅 쪽만 따로 20을 쓴다.
 */
const HERO_REFERENCE_ATK = 20;

/**
 * 영웅 쪽 관통 기준.
 *
 * 병종 쪽은 3을 쓴다(`PIERCE_REFERENCE_DEF`). 기준 공격력이 12와 20으로 갈린
 * 것과 같은 이유다. 영웅은 4티어 정예와 부딪히고 그쪽 방어가 6 안팎이라,
 * 병종 기준 3을 쓰면 관통의 값이 절반으로 깎여 보인다.
 */
export const HERO_REFERENCE_PIERCE = 6;

export function heroPower(s: Stats): number {
  // 관통은 상대 방어를 넘는 만큼 버려진다. 영웅이 만나는 상대의 방어가 6 안팎이다.
  const dps = ((s.atk + Math.min(s.pierce, HERO_REFERENCE_PIERCE)) * 30) / s.cd;
  const ehp = (s.hp * HERO_REFERENCE_ATK) / Math.max(1, HERO_REFERENCE_ATK - s.def);
  const mobility = 0.7 + (0.3 * s.speed) / 74;
  return Math.round(Math.sqrt(dps * ehp) * mobility);
}

/** 맨몸 대비 몇 퍼센트 올려주는가. */
export function equipGainPct(item: EquipDef): number {
  if (item.slot === Slot.Banner) return 0;
  const base = heroPower(BARE_HERO);
  const withIt = heroPower(heroStats({ [slotKey(item.slot)]: item.id }));
  return Math.round(((withIt - base) * 100) / base);
}

function slotKey(slot: Slot): keyof Loadout {
  return slot as keyof Loadout;
}

export function loadoutGainPct(loadout: Loadout): number {
  const base = heroPower(BARE_HERO);
  const withIt = heroPower(heroStats(loadout));
  return Math.round(((withIt - base) * 100) / base);
}

/** 이 물건 하나가 분대에 주는 보정. 보정이 없으면 기본값 그대로다. */
export function commandValue(item: EquipDef): CaptainBonus {
  return applyCommand(NO_CAPTAIN, [item.id]);
}

export function hasCommand(item: EquipDef): boolean {
  return item.command !== undefined;
}

// ---------------------------------------------------------------------------
// 얻기와 잃기
//
// 이 구획의 값은 전부 리플레이 규칙의 일부다. EQUIPMENT에 물건을 더하거나 빼는
// 것, TIER_SOURCE, dropChancePct의 곡선, WIPE_LOSS_PCT — 하나라도 바꾸면
// REPLAY_VERSION을 올린다. 서버가 같은 주사위를 굴려 다른 답을 얻으면 정직한
// 플레이어가 조작으로 몰린다.
// ---------------------------------------------------------------------------

/** 상점에 늘 있는 물건. 돈만 있으면 산다. */
export function shopStock(): EquipDef[] {
  // localeCompare는 환경의 로케일을 탄다. src/sim/ 안에서는 쓰지 않는다.
  return EQUIPMENT.filter((e) => e.source === Source.Shop).sort(
    (a, b) => a.tier - b.tier || (a.slot < b.slot ? -1 : a.slot > b.slot ? 1 : 0),
  );
}

/**
 * **상점이 파는 가장 높은 티어.** 그 계정이 가 본 가장 깊은 곳에서 얻을 수 있는 티어보다 **하나 아래**까지다 — 깊이 12를
 * 넘으면(4티어가 떨어진다) 3티어를, 깊이 20을 넘으면(5티어가 떨어진다) 4티어를 판다. 5티어는 팔지 않는다. 그 전에는
 * 예전처럼 2티어까지.
 *
 * 플레이한 사람이 청했다(2026-10-09): "상점 티어도 최고 전장에서 얻을 수 있는 것보다 한 티어 낮은 거까지는 구매할 수 있게
 * 해줘." 처음에는 3티어부터 돈으로 살 수 없었다("살 수 있으면 깊이 들어갈 이유가 사라진다") — 그런데 강화는 같은 물건을
 * 일곱 개 더 모아야 하고(`enhanceMaterials`), 전리품은 그 티어의 열댓 가지 가운데 하나가 나온다. 바라는 물건을 골라
 * 모을 길이 없었다. 가장 좋은 티어는 여전히 깊은 곳에서만 나오므로 깊이 들어갈 까닭은 남는다.
 */
export function shopTier(deepest: number): number {
  let top = 0;
  for (const t of DEPTH_TIERS) if (TIER_SOURCE[t].minDepth <= deepest) top = t;
  return Math.max(2, top - 1);
}

/** 전리품 티어를 상점에서 살 때의 값: 값어치(`cost`)의 몇 배인가. */
export const LOOT_PRICE_MULT = 2;

/**
 * **상점의 값.** 1·2티어(상점 물건)는 값어치 그대로(60·200냥), 전리품 티어는 값어치의 `LOOT_PRICE_MULT`배(3티어 1,400냥,
 * 4티어 4,000냥). 값어치(`cost` — 강화 값과 파는 값의 바탕)는 건드리지 않는다.
 *
 * 두 배로 둔 까닭(2026-10-09, 플레이한 사람이 근거를 물었다):
 * - **주운 것이 여전히 값지다.** 주우면 공짜인 것을 사려면 값어치의 두 배 — 파는 값은 값어치의 절반(`SELL_PCT`)이라
 *   샀다 팔면 4분의 3을 잃는다. 사고팔아 돈을 불리는 길은 없다.
 * - **그 티어가 열리는 때의 벌이에 견준 값이다.** 3티어가 열리는 깊이 12 언저리의 출정 한 번은 2,500냥쯤(세 판의 노획
 *   1,700 + 아장의 월급 800)이라 한 개가 출정 반 번, 4티어가 열리는 깊이 20 언저리는 5,000~6,000냥쯤이라 한 개가 출정
 *   한 번의 4분의 3쯤이다. 한두 개는 가볍게 사지만 ★3(일곱 개 더)은 무겁다: 바탕 하나를 이미 가진 3티어를 상점만으로
 *   ★3까지 올리면 재료 9,800 + 강화 2,450 = 12,250냥(바탕까지 사면 13,650), 4티어는 28,000 + 7,000 = 35,000냥(바탕까지
 *   39,000). 병사를 다시 세우고 진급시키는 돈과 다투는 크기다 — 전리품이 그 값을 깎아 준다.
 * 벌이의 숫자는 어림이고 **비용을 빼지 않은 총수입**(노획 + 월급)이다 — 급료·모집·진급을 낸 뒤에 남는 돈은 그보다 적다.
 * "출정 몇 번이면 ★3"이라고 말할 수 있는 값이 아니다. 기준 플레이어의 장부로 다시 재야 한다(리뷰).
 */
export function shopPrice(item: EquipDef): number {
  return item.source === Source.Depth ? item.cost * LOOT_PRICE_MULT : item.cost;
}

/**
 * 가 본 가장 깊은 곳이 `deepest`인 계정의 상점에 있는 물건(나라는 가리지 않는다 — `stash.shopFor`가 가린다). 상점 물건
 * 모두와, `shopTier`까지의 전리품 티어. 시즌 전용은 팔지 않는다.
 */
export function shopStockAt(deepest: number): EquipDef[] {
  const top = shopTier(deepest);
  return EQUIPMENT.filter((e) => e.source === Source.Shop || (e.source === Source.Depth && e.tier <= top)).sort(
    (a, b) => a.tier - b.tier || (a.slot < b.slot ? -1 : a.slot > b.slot ? 1 : 0),
  );
}

/**
 * 이 깊이에서 이 문화가 얻을 수 있는 물건들.
 *
 * 문화를 반드시 받는다. 안 걸러 주면 고구려 장수가 칠지도를 주워 놓고 차지 못한다.
 * 문화는 `ScenarioConfig.playerCulture`에 있으니 서버도 같은 후보를 재구성할 수 있다.
 */
export function dropCandidates(depth: number, culture: Culture): EquipDef[] {
  return EQUIPMENT.filter(
    (e) =>
      e.source === Source.Depth &&
      e.minDepth <= depth &&
      (e.culture === null || e.culture === culture),
  );
}

/**
 * 이 깊이에 열린 티어와 그 티어의 물건. 티어는 항상 오름차순이다.
 *
 * 문화가 들 수 없는 물건만 있는 티어는 아예 빠진다. 티어 우선 추첨이라 빠진
 * 티어의 몫은 남은 티어로 고르게 흩어지고, 한 자리가 헛돌지 않는다.
 */
export function dropTiers(
  depth: number,
  culture: Culture,
): { tier: number; weight: number; items: EquipDef[] }[] {
  const out: { tier: number; weight: number; items: EquipDef[] }[] = [];
  const pool = dropCandidates(depth, culture);
  for (const tier of DEPTH_TIERS) {
    const src = TIER_SOURCE[tier];
    if (src.minDepth > depth) continue;
    const items = pool.filter((e) => e.tier === tier);
    if (items.length === 0) continue;
    out.push({ tier, weight: src.weight, items });
  }
  return out;
}

/**
 * 얕은 곳에서는 아예 안 나오고 깊이 들어갈수록 자주 나온다.
 * 이 곡선이 완만하면 굳이 더 들어갈 이유가 없어진다.
 */
export function dropChancePct(depth: number): number {
  if (depth < DROP_MIN_DEPTH) return 0;
  // 한 깊이 +3%, 깊이 25에서 천장. 예전(+9%)은 깊이가 여섯 남짓이던 때의 기울기다.
  // 123: 바닥 10 → 25%, 천장 70 → 85%. 한 판에 두 번 굴리는 것(`DROP_ROLLS`)과 함께 — 강화에 같은 물건이 일곱 개 더
  // 드는데 전리품이 너무 드물었다(플레이한 사람: "장비를 좀 더 드롭해야 강화를 잘 할 수 있을 거 같아").
  return Math.min(85, 25 + (depth - DROP_MIN_DEPTH) * 3);
}

/**
 * 이긴 판 하나에 전리품을 굴리는 횟수. 굴림마다 따로 맞고 빗나간다 — 한 판에 둘까지 나온다. 관문은 첫 굴림이 반드시
 * 맞는다(관문 보상). 한 판에 나오는 수의 기댓값(`npm run equip`의 표):
 * - 관문이 아닌 깊이: 6에 0.56개(예전 0.13), 12에 0.92(0.31), 16에 1.16(0.43), 19에 1.34(0.52), 24에 1.64(0.67), 26부터 1.7(0.7).
 * - 관문(5의 배수 — 첫 굴림이 반드시 맞는다): 5에 1.25개, 10에 1.4, 15에 1.55, 20에 1.7, 25부터 1.85(예전에는 모두 1).
 * 처음에는 관문을 빼고 "20에 1.4, 25에 1.7"이라고 적었다(리뷰·QA가 재서 바로잡았다).
 */
export const DROP_ROLLS = 2;

/**
 * 전투 하나를 이겼을 때 무엇이 나오는가.
 *
 * 난수는 반드시 시드 PRNG를 받는다. 무엇이 떨어졌는지도 서버가 재현할 수 있어야
 * 하기 때문이다. 클라이언트가 "전설 무기가 나왔다"고 주장하는 것을 막는 방법은
 * 그 주사위를 서버가 다시 굴려보는 것뿐이다.
 *
 * 주사위는 결과와 상관없이 **늘 세 번** 굴린다. 빗나갔다고 덜 굴리면 뒤따르는
 * 판정이 통째로 밀려, 같은 시드인데 다른 답이 나온다. 낭비되는 두 번이 그 값이다.
 */
/**
 * 전장 특성마다 더 잘 떨어지는 칸(Todo5 7단계, "궁수대 판에서는 방패, 기마대 판에서는 창·말이 더 잘 나온다"). 그 판의
 * 적이 들고 있던 것을 거둔다는 뜻이다. 같은 티어 안에서 이 칸의 물건은 `FAVORED_WEIGHT`배로 뽑힌다 — 티어가 나올
 * 확률은 그대로다.
 */
export const TRAIT_LOOT: Readonly<Record<string, readonly Slot[]>> = {
  archers: [Slot.Shield],
  cavalry: [Slot.Mount, Slot.Weapon],
  heavy: [Slot.Armor],
  elite: [Slot.Weapon, Slot.Armor],
  rain: [Slot.Helm],
  night: [Slot.Banner],
  // 안개도 군기(뿔나팔). 처음에는 빠져 있어 "안개 판에서 더 잘 나온다"는 약속이 헛말이었다(리뷰가 짚었다).
  fog: [Slot.Banner],
};
export const FAVORED_WEIGHT = 3;

export function rollDrop(
  rng: Rng,
  depth: number,
  culture: Culture,
  sure = false,
  traits: readonly string[] = [],
): EquipDef | null {
  // `sure`: 관문 보상 — 반드시 하나 떨어진다. 주사위는 같은 수만큼 굴린다(뒤따르는 소실 판정의 수열이 흔들리지 않게).
  const hit = rng.int(100) < dropChancePct(depth) || sure;
  const tiers = dropTiers(depth, culture);

  let total = 0;
  for (const t of tiers) total += t.weight;
  // n이 0이면 int()가 NaN을 낸다. 뽑을 게 없어도 주사위 수는 맞춰야 한다.
  let pick = rng.int(total > 0 ? total : 1);

  let chosen = tiers.length > 0 ? tiers[tiers.length - 1] : null;
  for (const t of tiers) {
    pick -= t.weight;
    if (pick < 0) {
      chosen = t;
      break;
    }
  }

  const items = chosen ? chosen.items : [];
  // 같은 티어 안에서는 균등하되, 그 판의 특성이 부르는 칸(`TRAIT_LOOT`)은 무겁게. 물건을 더 넣어도 그 티어가 나올
  // 확률은 그대로다. 주사위는 한 번이다(특성이 없어도 같은 횟수).
  const favored = new Set<Slot>();
  for (const t of traits) for (const s of TRAIT_LOOT[t] ?? []) favored.add(s);
  const weightOf = (e: EquipDef) => (favored.has(e.slot) ? FAVORED_WEIGHT : 1);
  let wsum = 0;
  for (const e of items) wsum += weightOf(e);
  let roll = rng.int(wsum > 0 ? wsum : 1);
  if (!hit || total <= 0 || items.length === 0) return null;
  for (const e of items) {
    roll -= weightOf(e);
    if (roll < 0) return e;
  }
  return items[items.length - 1];
}

/**
 * 전멸했을 때 장비를 잃을 확률(백분율).
 *
 * 전멸은 이미 데려간 병력을 전부 잃고 전과도 0이 되는 상황이다. 거기에 장비까지
 * 무조건 잃게 하면 이중 처벌이 되어 복구가 불가능해진다. 그렇다고 아무것도 안
 * 잃으면 깊이 들어가는 위험에서 장비가 빠져 계산이 한쪽으로 기운다.
 * 그래서 확률로 둔다. 잃을 수도 있다는 것만으로 판단이 달라진다.
 */
export const WIPE_LOSS_PCT = 35;

/**
 * 전멸 시 잃는 칸을 고른다. 비어 있던 칸도 포함해 **칸마다 한 번씩** 굴린다.
 *
 * 처음에는 빈 칸과 모르는 식별자를 건너뛰었다. 그러면 굴리는 횟수가 무엇을
 * 차고 있었느냐에 따라 달라져, 물건 하나가 목록에서 빠지는 것만으로 그 뒤 칸의
 * 판정이 전부 밀린다. 지금은 늘 SLOT_ORDER만큼 굴리고 결과만 버린다.
 *
 * 물건 식별자가 아니라 칸을 돌려주는 이유는, 장수와 동료가 같은 목록을 쓰기
 * 때문이다. 같은 환두대도를 둘이 차고 있으면 식별자만으로는 누구 것이 사라졌는지
 * 말할 수 없다.
 *
 * 전멸 시에는 장수부터 굴리고 그다음 동료를 분대 색인 오름차순으로 굴린다.
 * 이 순서가 곧 난수 수열이다.
 */
export function rollWipeLoss(rng: Rng, worn: Loadout): Slot[] {
  const lost: Slot[] = [];
  for (const slot of SLOT_ORDER) {
    const hit = rng.int(100) < WIPE_LOSS_PCT;
    const id = worn[slot];
    if (hit && id && equipOf(id)) lost.push(slot);
  }
  return lost;
}

export function equipmentOfSlot(slot: Slot): EquipDef[] {
  return EQUIPMENT.filter((e) => e.slot === slot).sort((a, b) => a.tier - b.tier);
}

/** 병종 쪽 계산과 같은 식을 쓰는지 확인하는 데 쓴다. */
export { computePower, Branch };
